import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Article as ArticleIcon,
  ContentPaste as ContentIcon,
  ContactMail as ContactIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../Logo.png';
import NotificationBell from './NotificationBell';

const Sidebar = ({ open, onClose, variant }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [collapsed, setCollapsed] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout, isAdmin, isSuperAdmin } = useAuth();

  const getMenuItems = () => {
    const commonItems = [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin/dashboard', allowed: true },
      { text: 'Blogs', icon: <ArticleIcon />, path: '/admin/blogs', allowed: true },
      { text: 'Content', icon: <ContentIcon />, path: '/admin/content', allowed: true },
      { text: 'Contact Control', icon: <ContactIcon />, path: '/admin/contact-control', allowed: true },
      { text: 'Profile', icon: <PersonIcon />, path: '/admin/profile', allowed: true },
      { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings', allowed: true },
    ];

    if (isSuperAdmin) {
      commonItems.unshift(
        { text: 'User Control', icon: <PeopleIcon />, path: '/admin/users', allowed: true },
        { text: 'Admin Approvals', icon: <VerifiedUserIcon />, path: '/admin/approvals', allowed: true }
      );
    }

    return commonItems.filter(item => item.allowed);
  };

  const menuItems = getMenuItems();

  const handleNavigation = (path) => {
    navigate(path);
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
    if (variant === 'temporary' && onClose) {
      onClose();
    }
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const drawerWidth = collapsed ? 80 : 280;

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Logo & Header */}
      <Box sx={{ 
        p: collapsed ? 2 : 3, 
        textAlign: 'center', 
        borderBottom: 1, 
        borderColor: 'divider',
        transition: theme.transitions.create(['padding'], {
          duration: theme.transitions.duration.shorter,
        }),
      }}>
        <Box sx={{ position: 'relative', display: 'inline-block' }}>
          <Avatar
            src={Logo}
            sx={{ 
              width: collapsed ? 40 : 70, 
              height: collapsed ? 40 : 70, 
              mx: 'auto', 
              mb: collapsed ? 1 : 2,
              transition: theme.transitions.create(['width', 'height', 'margin'], {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          />
         
        </Box>
        
        {!collapsed && (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              AediaX Admin
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentUser?.email}
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              {isSuperAdmin && (
                <Chip 
                  label="Super Admin" 
                  size="small" 
                  color="secondary" 
                  icon={<StarIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontWeight: 600 }}
                />
              )}
              {isAdmin && !isSuperAdmin && (
                <Chip 
                  label="Administrator" 
                  size="small" 
                  color="primary" 
                  icon={<SecurityIcon sx={{ fontSize: 14 }} />}
                />
              )}
            </Stack>
          </>
        )}
         <NotificationBell />
        
        {collapsed && (
          <Tooltip title={isSuperAdmin ? "Super Admin" : "Administrator"} placement="right">
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              {isSuperAdmin && (
                <StarIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
              )}
              {isAdmin && !isSuperAdmin && (
                <SecurityIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
              )}
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Collapse Toggle Button - Desktop only */}
      {isDesktop && variant === 'permanent' && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: collapsed ? 'center' : 'flex-end', 
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <IconButton 
            onClick={handleToggleCollapse}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              },
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}

      {/* Menu Items */}
      <List sx={{ flex: 1, px: collapsed ? 1 : 2, py: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <Tooltip 
              title={collapsed ? item.text : ''} 
              placement="right"
              disableHoverListener={!collapsed}
            >
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                selected={isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 2,
                  py: collapsed ? 1.5 : 1,
                  minHeight: 48,
                  transition: theme.transitions.create(['padding', 'justify-content'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                  '&.Mui-selected': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.25),
                    },
                    '& .MuiListItemIcon-root': {
                      color: theme.palette.primary.main,
                    },
                    '& .MuiListItemText-primary': {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    },
                  },
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: collapsed ? 'auto' : 40,
                    mr: collapsed ? 0 : 2,
                    justifyContent: 'center',
                    color: isActive(item.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                    transition: theme.transitions.create(['margin', 'minWidth'], {
                      duration: theme.transitions.duration.shorter,
                    }),
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText 
                    primary={item.text}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: {
                        fontWeight: isActive(item.path) ? 600 : 400,
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      <Divider />

      {/* Footer */}
      <Box sx={{ p: collapsed ? 1 : 2 }}>
        <Tooltip title={collapsed ? "Logout" : ""} placement="right" disableHoverListener={!collapsed}>
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2,
                py: collapsed ? 1.5 : 1,
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  minWidth: collapsed ? 'auto' : 40,
                  mr: collapsed ? 0 : 2,
                  justifyContent: 'center',
                  color: theme.palette.error.main,
                  transition: theme.transitions.create(['margin', 'minWidth'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              {!collapsed && (
                <ListItemText 
                  primary="Logout"
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
      </Box>
    </Box>
  );

  // Temporary drawer for mobile
  if (variant === 'temporary') {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Permanent drawer for desktop with collapse
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: 'none', lg: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'fixed',
          height: '100vh',
          top: 0,
          left: 0,
          transition: theme.transitions.create(['width'], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.sharp,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;